import React, { type FC, useEffect } from 'react';
import { Button, Dropdown, Form, Tooltip } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useExportPreset, { type ExportPresets, ExportPresetValue } from 'packages/hooks/useExportPreset';

export type PresetData = {
    preset: ExportPresets;
    bynderAccountId: number;
};

type PresetProps = {
    isExporting?: boolean;
    isLoadingRenditions?: boolean;
    isDownloadBlocked?: boolean;
    isWaitingRoomBlocked?: boolean;
    onSelect: (data: PresetData) => void;
};

const Preset: FC<PresetProps> = ({
    isExporting,
    isLoadingRenditions,
    isDownloadBlocked,
    isWaitingRoomBlocked,
    onSelect,
}) => {
    const { translate } = useTranslate();

    const { presets, selectedPreset, setSelectedPreset, bynderAccountId } = useExportPreset({
        isDownloadBlocked,
        isWaitingRoomBlocked,
    });

    const getDisableTooltip = (preset) => {
        if (preset.value === ExportPresetValue.BYNDER) {
            return translate('modal.export.waiting-room-only.tooltip');
        }

        if (preset.value === ExportPresetValue.DOWNLOAD) {
            return translate('modal.export.download-only.tooltip');
        }

        return '';
    };

    useEffect(() => {
        onSelect({
            preset: selectedPreset,
            bynderAccountId,
        });
    }, [selectedPreset, bynderAccountId, onSelect]);

    return (
        <Form.Group>
            <Form.Label>{translate('modal.quick-export.preset')}</Form.Label>
            <Dropdown
                trigger={({ isOpen, ...triggerProps }) => (
                    <Button
                        isPressed={isOpen}
                        isDisabled={isExporting}
                        icon={selectedPreset.icon}
                        rightIcon={<Dropdown.Arrow />}
                        {...triggerProps}
                        isFullWidth
                    >
                        <Dropdown.SelectButtonText>
                            {isLoadingRenditions ? translate('modal.quick-export.loading') : selectedPreset?.label}
                        </Dropdown.SelectButtonText>
                    </Button>
                )}
                position="bottom"
            >
                {presets.map((preset) =>
                    preset.disabled ? (
                        <Tooltip content={getDisableTooltip(preset)} key={preset.value} position="bottom-left">
                            <Dropdown.Item
                                key={preset.value}
                                onClick={() => setSelectedPreset(preset)}
                                isChecked={preset.value === selectedPreset.value}
                                isDisabled={preset.disabled}
                                icon={preset.icon}
                            >
                                {preset.label}
                            </Dropdown.Item>
                        </Tooltip>
                    ) : (
                        <Dropdown.Item
                            key={preset.value}
                            onClick={() => setSelectedPreset(preset)}
                            isChecked={preset.value === selectedPreset.value}
                            isDisabled={preset.disabled}
                            icon={preset.icon}
                        >
                            {preset.label}
                        </Dropdown.Item>
                    ),
                )}
            </Dropdown>
        </Form.Group>
    );
};

export default Preset;
